'use client';

import type { AgentOption, AgentOptionsPayload } from '@/@studio-ai/types';
import { humanizeLabel } from '@/@studio-ai/utils/text-utils';
import { Button, Chip, Input } from '@heroui/react';
import { Icon } from '@iconify/react';
import { AnimatePresence, motion } from 'framer-motion';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Chip-based rendering of an `agent_options` payload.
 */
function OptionChipsImpl({
    pendingOptions,
    onSelectOption,
    onSelectMultiple,
    onSkipOptions,
}: {
    pendingOptions: AgentOptionsPayload;
    onSelectOption?: (option: AgentOption) => void;
    onSelectMultiple?: (options: AgentOption[]) => void;
    onSkipOptions?: () => void;
}) {
    const allowMultiple = pendingOptions.allow_multiple ?? false;
    const allowSkip = pendingOptions.allow_skip ?? false;
    const allowCustom = pendingOptions.allow_custom ?? false;

    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [customOptions, setCustomOptions] = useState<AgentOption[]>([]);
    const [addMoreOpen, setAddMoreOpen] = useState(false);
    const [addMoreText, setAddMoreText] = useState('');
    const addMoreInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        setSelectedIds(new Set());
        setCustomOptions([]);
        setAddMoreOpen(false);
        setAddMoreText('');
    }, [pendingOptions]);

    useEffect(() => {
        if (addMoreOpen) {
            requestAnimationFrame(() => addMoreInputRef.current?.focus());
        }
    }, [addMoreOpen]);

    const visibleOptions = useMemo(
        () => [...(pendingOptions.options ?? []), ...customOptions],
        [pendingOptions, customOptions],
    );

    const handleChipPress = useCallback(
        (option: AgentOption) => {
            if (allowMultiple) {
                setSelectedIds((prev) => {
                    const next = new Set(prev);
                    if (next.has(option.id)) next.delete(option.id);
                    else next.add(option.id);
                    return next;
                });
                return;
            }
            onSelectOption?.(option);
        },
        [allowMultiple, onSelectOption],
    );

    const handleAddMore = useCallback(() => {
        const trimmed = addMoreText.trim();
        if (!trimmed || !allowMultiple || !allowCustom) return;
        const id = `custom_${Date.now()}`;
        setCustomOptions((prev) => [...prev, { id, label: trimmed }]);
        setSelectedIds((prev) => {
            const next = new Set(prev);
            next.add(id);
            return next;
        });
        setAddMoreText('');
        setAddMoreOpen(false);
    }, [addMoreText, allowMultiple, allowCustom]);

    const handleConfirm = useCallback(() => {
        if (selectedIds.size === 0) return;
        const selected = visibleOptions.filter((o) => selectedIds.has(o.id));
        onSelectMultiple?.(selected);
    }, [selectedIds, visibleOptions, onSelectMultiple]);

    if (visibleOptions.length === 0 && !allowSkip) return null;

    return (
        <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            transition={{ duration: 0.15 }}
            className="flex w-full flex-wrap items-center gap-2"
            role="group"
            aria-label={pendingOptions.options?.[0]?.label ?? 'Suggested answers'}
        >
            {visibleOptions.map((option) => {
                const isChecked = selectedIds.has(option.id);
                const isCustom = option.id.startsWith('custom_');
                return (
                    <Chip
                        key={option.id}
                        as="button"
                        type="button"
                        variant={isChecked ? 'solid' : 'flat'}
                        size="sm"
                        onClick={() => handleChipPress(option)}
                        endContent={
                            allowMultiple && isChecked ? (
                                <Icon icon="lucide:check" width={12} height={12} className="ml-1" />
                            ) : undefined
                        }
                        classNames={{
                            base: `h-7 cursor-pointer transition-colors px-3 py-5 ${
                                isChecked
                                    ? 'bg-foreground text-background'
                                    : 'bg-default-100 hover:bg-default-200'
                            }`,
                            content: 'text-mini font-semibold',
                        }}
                    >
                        {humanizeLabel(option.label)}
                        {isCustom ? ' · custom' : ''}
                    </Chip>
                );
            })}

            {allowMultiple && allowCustom && (
                <AnimatePresence mode="wait" initial={false}>
                    {addMoreOpen ? (
                        <motion.div
                            key="add-more-input"
                            initial={{ opacity: 0, width: 0 }}
                            animate={{ opacity: 1, width: 'auto' }}
                            exit={{ opacity: 0, width: 0 }}
                            transition={{ duration: 0.12 }}
                            className="flex items-center gap-1"
                        >
                            <Input
                                ref={addMoreInputRef}
                                size="lg"
                                variant="bordered"
                                placeholder="Add your own…"
                                value={addMoreText}
                                onValueChange={setAddMoreText}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        handleAddMore();
                                    } else if (e.key === 'Escape') {
                                        e.preventDefault();
                                        setAddMoreOpen(false);
                                        setAddMoreText('');
                                    }
                                }}
                                onBlur={() => {
                                    if (!addMoreText.trim()) setAddMoreOpen(false);
                                }}
                                classNames={{
                                    input: 'text-tiny',
                                    inputWrapper: 'h-7 min-h-7 px-2',
                                    base: 'w-40',
                                }}
                            />
                            {addMoreText.trim() && (
                                <Button
                                    isIconOnly
                                    size="sm"
                                    variant="flat"
                                    className="h-7 w-7 min-w-0"
                                    onPress={handleAddMore}
                                    aria-label="Add custom option"
                                >
                                    <Icon icon="lucide:check" width={14} height={14} />
                                </Button>
                            )}
                        </motion.div>
                    ) : (
                        <Chip
                            key="add-more-chip"
                            as="button"
                            type="button"
                            size="sm"
                            variant="bordered"
                            startContent={
                                <Icon icon="lucide:plus" width={12} height={12} className="ml-1" />
                            }
                            onClick={() => setAddMoreOpen(true)}
                            classNames={{
                                base: 'h-7 cursor-pointer border-dashed border-default-300 hover:bg-default-100 px-3 py-5',
                                content: 'text-mini font-medium text-default-500',
                            }}
                        >
                            Add more
                        </Chip>
                    )}
                </AnimatePresence>
            )}

            {allowMultiple && (
                <Button
                    size="sm"
                    isDisabled={selectedIds.size === 0}
                    onPress={handleConfirm}
                    className="ml-1 min-w-0 bg-mfour-green px-3 py-5 text-mini text-white"
                    endContent={<Icon icon="lucide:arrow-right" width={12} height={12} />}
                    radius="full"
                >
                    Looks Good ({selectedIds.size})
                </Button>
            )}

            {allowSkip && (
                <Chip
                    as="button"
                    type="button"
                    size="sm"
                    variant="light"
                    onClick={() => onSkipOptions?.()}
                    classNames={{
                        base: 'h-7 cursor-pointer hover:bg-default-100',
                        content: 'px-1 text-tiny font-medium text-default-500',
                    }}
                >
                    Skip
                </Chip>
            )}
        </motion.div>
    );
}

export const OptionChips = memo(OptionChipsImpl);
